import Typography from "@mui/material/Typography";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import UnmatchedStudentsDialog from "./UnmatchedStudentsDialog";
import { updateStudentExamRecords } from "../../../components/Api/ExamGradingEndpoints";
import DownloadResultsDialog from "./DownloadResultsDialog";
import ClassListCheckDialog from "./ClassListCheckDialog";
import { useAtom, useAtomValue } from "jotai";
import {
	currentOmrRequestAtom,
	examProcessingStateAtom,
	allStudentsMatchedAtom,
	examInDatabaseAtom,
	classListFileAtom,
	gradeWithoutMatchingAtom,
} from "../../../components/Atoms/atoms";
import CustomDataGridToolbar from "./CustomDataGridToolbar";

export default function ClassAndScores({
	rescoreLoading,
	setRescoreLoading,
	auth,
	setSnackbar,
	handleDownloadCsv,
	handleRescoreExam,
	editMode,
	token,
	reqnum,
	fetchRequestDetails,
	getCsvBlobs,
	openDownloadDialog,
	setOpenDownloadDialog,
	classListError,
}) {
	const examInDatabase = useAtomValue(examInDatabaseAtom);
	const [examProcessingState, setExamProcessingState] = useAtom(examProcessingStateAtom);
	const [allStudentsMatched, setAllStudentsMatched] = useAtom(allStudentsMatchedAtom);
	const currentOmrRequest = useAtomValue(currentOmrRequestAtom);
	const [loadingUpdateStudentRecords, setLoadingUpdateStudentRecords] = useState(false);
	const [openUnmatchedDialog, setOpenUnmatchedDialog] = useState(false);
	const [unmatchedStudents, setUnmatchedStudents] = useState([]);

	const [loadingClassList, setLoadingClassList] = useState(false);

	const [openClassListCheckDialog, setOpenClassListCheckDialog] = useState(false);
	const [classListDialogMode, setClassListDialogMode] = useState("invalid");

	const classListFile = useAtomValue(classListFileAtom);
	const matchingStartStudentsRef = useRef(null);

	const [gradeWithoutMatching, setGradeWithoutMatching] = useAtom(gradeWithoutMatchingAtom);

	const handleChangeGradeWithoutMatching = (event) => {
		const checked = event.target.checked;
		setGradeWithoutMatching(checked);
	};

	const handleCloseClassListCheckDialog = useCallback(() => {
		setOpenClassListCheckDialog(false);

		setClassListDialogMode("invalid");
	}, []);

	const handleOpenClassListCheckDialog = useCallback(() => {
		setClassListDialogMode("manual");
		setOpenClassListCheckDialog(true);
	}, []);

	useEffect(() => {
		if (
			classListError?.error &&
			classListError?.classid === currentOmrRequest?.class_ids &&
			currentOmrRequest?.class_ids &&
			!openClassListCheckDialog
		) {
			setClassListDialogMode("invalid");
			setOpenClassListCheckDialog(true);
		}
	}, [
		classListError?.error,
		classListError?.classid,
		currentOmrRequest?.class_ids,
		openClassListCheckDialog,
	]);

	useEffect(() => {
		if (
			openClassListCheckDialog &&
			classListDialogMode === "invalid" &&
			classListFile.class_ids === currentOmrRequest?.class_ids &&
			classListFile.classlist?.length > 0
		) {
			handleCloseClassListCheckDialog();
		}
	}, [
		openClassListCheckDialog,
		classListDialogMode,
		classListFile.class_ids,
		classListFile.classlist,
		currentOmrRequest?.class_ids,
		handleCloseClassListCheckDialog,
	]);

	useEffect(() => {
		setOpenClassListCheckDialog(false);
		setClassListDialogMode("invalid");
	}, [currentOmrRequest?.reqnum]);

	// Create a stable fingerprint of answer keys to detect changes
	const answerKeysFingerprint = useMemo(() => {
		return JSON.stringify(
			Object.values(examProcessingState?.answer_keys || {}).map((k) => ({
				version: k.version,
				items: (k.items || []).map((it) => ({
					item_no: it.item_no,
					omit: it.omit,
					modified_answer: it.modified_answer,
				})),
			})),
		);
	}, [examProcessingState?.answer_keys]);

	const fetchAndRescore = async () => {
		setRescoreLoading(true);
		await handleRescoreExam();
		setRescoreLoading(false);
	};

	// Rescore when answer keys change (after modifications are saved)
	useEffect(() => {
		if (examProcessingState?.answer_keys) {
			fetchAndRescore();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [answerKeysFingerprint]);

	const handleAbortMatching = () => {
		const snapshot = matchingStartStudentsRef.current;
		if (Array.isArray(snapshot) && examProcessingState) {
			setExamProcessingState((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					students: cloneStudentsForRollback(snapshot),
				};
			});
		}
		setUnmatchedStudents([]);
		setOpenUnmatchedDialog(false);
		matchingStartStudentsRef.current = null;
	};

	const handleWrongClassId = () => {
		handleAbortMatching();
		setClassListDialogMode("manual");
		setOpenClassListCheckDialog(true);
	};

	const handleUpdateStudentsInDB = async () => {
		setLoadingUpdateStudentRecords(true);
		try {
			const allStudents = examProcessingState?.students ?? [];
			const changedStudents = getChangedStudentsForPersistence(
				allStudents,
				matchingStartStudentsRef.current,
			);
			if (
				Array.isArray(matchingStartStudentsRef.current) &&
				allStudents.length > 0 &&
				changedStudents.length === 0
			) {
				await handleRescoreExam(examProcessingState);
				setAllStudentsMatched(true);
				setUnmatchedStudents([]);
				setOpenUnmatchedDialog(false);
				matchingStartStudentsRef.current = null;
				return { status: 200, data: { skipped_update: true } };
			}
			const studentsToPersist = changedStudents.length > 0 ? changedStudents : allStudents;
			const response = await updateStudentExamRecords(
				token,
				examProcessingState.header.reqnum,
				studentsToPersist,
			);
			if (response.status === 200) {
				const refreshedExamProcessingState = await fetchRequestDetails();
				await handleRescoreExam(refreshedExamProcessingState);
				setAllStudentsMatched(true);
				setUnmatchedStudents([]);
				setOpenUnmatchedDialog(false);
				matchingStartStudentsRef.current = null;
			}
			return response;
		} finally {
			setLoadingUpdateStudentRecords(false);
		}
	};

	const renderStudentName = (params) => {
		if (params.row.std_name_matched) {
			return (
				<div className="flex flex-col justify-center h-full">
					<Typography variant="body1" className="line-clamp-1">
						{params.row.std_name_matched}
					</Typography>
					<Typography className="text-gray-500" sx={{ fontSize: "0.70rem" }}>
						{params.row.std_name_raw}
					</Typography>
				</div>
			);
		}
		return params.row.std_name_raw;
	};

	const renderStudentID = (params) => {
		if (params.row.std_id_matched) {
			return (
				<div className="flex flex-col justify-center h-full">
					<Typography variant="body1" className="line-clamp-1">
						{params.row.std_id_matched}
					</Typography>
					<Typography className="text-gray-500" sx={{ fontSize: "0.70rem" }}>
						{params.row.std_id_raw}
					</Typography>
				</div>
			);
		}
		return params.row.std_id_raw;
	};

	const renderStudentScore = (params) => {
		if (
			params.row.modified_score &&
			params.row.original_score &&
			Number(params.row.modified_score) !== Number(params.row.original_score)
		) {
			return (
				<div className="flex flex-col justify-center h-full">
					<Typography variant="body1" className="line-clamp-1">
						{params.row.modified_score}
					</Typography>
					<Typography className="text-gray-500" sx={{ fontSize: "0.70rem" }}>
						{params.row.original_score}
					</Typography>
				</div>
			);
		}
		return params.row.original_score;
	};

	const columns = [
		{
			field: "std_name_matched",
			headerName: "Name",
			width: 200,
			align: "",
			renderCell: renderStudentName,
		},
		{
			field: "std_id_matched",
			headerName: "ID",
			width: 90,
			renderCell: renderStudentID,
		},
		{
			field: "ccid",
			headerName: "CCID",
			width: 90,
		},
		{
			field: "original_score",
			headerName: "Score",
			width: 90,
			renderCell: renderStudentScore,
		},

		{ field: "version", headerName: "Version", width: 70 },
		{ field: "items_wrong", headerName: "Wrongs", width: 100, flex: 0.5 },
		{
			field: "scored_responses",
			headerName: "Scored Responses",
			width: 100,
			flex: 0.5,
		},
	];

	// const TextInput = forwardRef(function TextInput(props, ref) {
	// 	return (
	// 		<input
	// 			ref={ref}
	// 			{...props}
	// 			className={clsx(
	// 				"h-9 w-full rounded border border-neutral-200  px-2.5 text-base text-neutral-900  focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary",
	// 				props.className
	// 			)}
	// 		/>
	// 	);
	// });

	const handleMatchStudents = async () => {
		if (classListFile?.classlist.length > 0) {
			matchingStartStudentsRef.current = cloneStudentsForRollback(examProcessingState?.students);
			setOpenUnmatchedDialog(true);
		}
	};

	const handleOpenDownloadDialog = async () => {
		setRescoreLoading(true);
		await handleRescoreExam();
		setRescoreLoading(false);
		setOpenDownloadDialog(true);
	};

	function appendSuffixToFilename(filename, filenameSuffix = "") {
		if (!filenameSuffix || !filename) return filename;
		const dotIndex = filename.lastIndexOf(".");
		if (dotIndex <= 0) return `${filename}${filenameSuffix}`;
		return `${filename.slice(0, dotIndex)}${filenameSuffix}${filename.slice(dotIndex)}`;
	}

	function getLogsBlob({ examProcessingState, classListFile, filenameSuffix = "" }) {
		const responses = examProcessingState?.students ?? [];
		const txt = buildMatchLogText(responses, classListFile); // your existing helper
		const name = appendSuffixToFilename(
			`${examProcessingState?.header?.reqnum}_log.txt`,
			filenameSuffix,
		);
		const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
		return { name, blob };
	}

	const exportLogsOnFinish = ({ examProcessingState, classListFile, filenameSuffix = "" }) => {
		const { name, blob } = getLogsBlob({
			examProcessingState,
			classListFile: classListFile.classlist,
			filenameSuffix,
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = name;
		a.click();
		URL.revokeObjectURL(url);
	};

	const isDataSameAsRequest = examProcessingState?.students.every(
		(s) => s.reqnum === currentOmrRequest.reqnum,
	);

	return (
		<div>
			<DownloadResultsDialog
				open={openDownloadDialog}
				setOpen={setOpenDownloadDialog}
				examProcessingState={examProcessingState}
				token={token}
				reqnum={reqnum}
				handleDownloadCsv={handleDownloadCsv}
				exportLogsOnFinish={exportLogsOnFinish}
				getLogsBlob={getLogsBlob}
				getCsvBlobs={getCsvBlobs}
				auth={auth}
				setSnackbar={setSnackbar}
				fetchRequestDetails={fetchRequestDetails}
			/>
			<UnmatchedStudentsDialog
				open={openUnmatchedDialog}
				setOpen={setOpenUnmatchedDialog}
				unmatchedStudents={unmatchedStudents}
				setUnmatchedStudents={setUnmatchedStudents}
				handleUpdateStudentsInDB={handleUpdateStudentsInDB}
				loadingUpdateStudentRecords={loadingUpdateStudentRecords}
				token={token}
				handleClose={handleAbortMatching}
				exportLogsOnFinish={exportLogsOnFinish}
				handleWrongClassId={handleWrongClassId}
			/>
			<ClassListCheckDialog
				open={openClassListCheckDialog}
				onClose={handleCloseClassListCheckDialog}
				mode={classListDialogMode}
				token={token}
				fetchRequestDetails={fetchRequestDetails}
				setLoadingClassList={setLoadingClassList}
			/>
			<div className="flex flex-col overflow-y-auto bg-white relative">
				{editMode && (
					<div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center bg-gray-500 opacity-80 z-20  hover:cursor-not-allowed">
						<p className="text-white text-center mx-auto  font-semibold my-auto  px-4 py-2 bg-black rounded-md">
							Answer keys are being modified. Either save changes or discard them to get results.
						</p>
					</div>
				)}
				<div className="text-gray-800  py-2">
					Student Responses ({examProcessingState?.students.length || 0})
				</div>
				<div className="h-[75dvh]">
					{isDataSameAsRequest && (
						<DataGrid
							sx={{
								"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
									outline: "None !important",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
									width: "0.4em",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
									background: "#f1f1f1",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
									backgroundColor: "#888",
								},
								"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
									background: "#555",
								},

								borderRadius: "0px 4px 4px 0px",
							}}
							className="requisitions-table"
							autoPageSize
							loading={rescoreLoading}
							disableSelectionOnClick
							showToolbar
							initialState={{
								sorting: {
									sortModel: [{ field: "std_name_matched", sort: "asc" }],
								},
							}}
							slots={{
								// footer: CustomFooter,
								toolbar: CustomDataGridToolbar,
								loadingOverlay: LinearProgress,
							}}
							slotProps={{
								toolbar: {
									handleMatchStudents,
									handleOpenDownloadDialog,
									handleChangeGradeWithoutMatching,
									handleOpenClassListCheckDialog,
									loadingClassList,
									allStudentsMatched,
									classListFile,
									examProcessingState,
									reqnum,
									examInDatabase,
									gradeWithoutMatching,
									handleRescoreExam,
								},
							}}
							rows={examProcessingState?.students}
							density="standard"
							columns={columns}
							getRowId={(row) => row.rsp_id}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
const cloneStudentsForRollback = (students = []) =>
	Array.isArray(students) ? students.map((student) => ({ ...student })) : [];

const normalizePersistedStudentMatch = (student = {}) => {
	const matchType = student?.match_type ? String(student.match_type) : null;
	const matchedId =
		student?.std_id_matched !== undefined &&
		student?.std_id_matched !== null &&
		String(student.std_id_matched).trim() !== ""
			? String(student.std_id_matched).trim()
			: null;
	const matchedName = student?.std_name_matched
		? String(student.std_name_matched).trim() || null
		: null;
	const matchedCcid = student?.ccid ? String(student.ccid).trim() || null : null;
	const hasDirectMatch = Boolean(matchedId && matchedName && matchType !== "cannot-match");

	if (hasDirectMatch) {
		return {
			std_id_matched: matchedId,
			std_name_matched: matchedName,
			ccid: matchedCcid,
			match_attempted: "Y",
			match_type: matchType,
		};
	}

	return {
		std_id_matched: null,
		std_name_matched: null,
		ccid: null,
		match_attempted: "Y",
		match_type: matchType,
	};
};

const persistedMatchStateEqual = (left, right) => {
	const a = normalizePersistedStudentMatch(left);
	const b = normalizePersistedStudentMatch(right);
	return (
		a.std_id_matched === b.std_id_matched &&
		a.std_name_matched === b.std_name_matched &&
		a.ccid === b.ccid &&
		a.match_type === b.match_type &&
		a.match_attempted === b.match_attempted
	);
};

const getChangedStudentsForPersistence = (currentStudents = [], baselineStudents = []) => {
	if (!Array.isArray(currentStudents) || currentStudents.length === 0) return [];
	if (!Array.isArray(baselineStudents)) return currentStudents;

	const baselineBySheetSequence = new Map(
		baselineStudents.map((student) => [String(student?.sheet_sequence ?? ""), student]),
	);

	return currentStudents.filter((student) => {
		const key = String(student?.sheet_sequence ?? "");
		const baseline = baselineBySheetSequence.get(key);
		if (!baseline) return true;
		return !persistedMatchStateEqual(student, baseline);
	});
};

// --- utils: padding / formatting ---
const pad = (s = "", n = 28) => (s + " ".repeat(n)).slice(0, n);
const padRight = (s = "", n = 10) => (String(s) + " ".repeat(n)).slice(0, n);

// produce "Last, First (ID) [ccid]" from classlist row
const displayClasslist = (row) =>
	row ? `${row[4]}, ${row[3]} (${row[6]})${row[2] ? ` [${row[2]}]` : ""}` : "";

// fast index by student ID (column 6 in classListFile)
const makeClassById = (classListFile) => new Map(classListFile?.map((r) => [String(r[6]), r]));
// --- TXT LOG BUILDER (mirrors your old sections) ---
function buildMatchLogText(responses, classListFile) {
	const classById = makeClassById(classListFile);

	const ts = new Date().toISOString().replace("T", " ").replace(/\..+/, "");
	const headerLines = [];
	headerLines.push(`Exam Matching Log`);
	headerLines.push(`Timestamp: ${ts}`);
	headerLines.push("");

	// buckets
	const exact = [];
	const close = [];
	const manual = [];
	const cannot = [];
	const unmatched = [];

	for (const r of responses) {
		if (r.match_attempted) {
			const cRow = classById.get(String(r.std_id_matched));
			const left = `${padRight(r.std_id_raw ?? r.std_id_matched ?? "", 10)}  ${pad(
				(r.std_name_raw ?? "").toUpperCase(),
				28,
			)}`;
			const right =
				displayClasslist(cRow) || `${r.std_name_matched ?? ""} (${r.std_id_matched ?? ""})`;
			const line = `${left}  ->  ${right}`;
			const t = String(r.match_type || "");
			if (t === "exact-id-and-name") exact.push(line);
			else if (
				t === "exact-id-fuzzy-name" ||
				t === "exact-id-partial-name" ||
				t === "exact-id-and-one-name" ||
				t === "exact-id-last-token"
			)
				close.push(`${line}    [${t}]`);
			else if (t === "cannot-match") {
				const left = `${padRight(r.std_id_raw ?? "", 10)}  ${(r.std_name_raw ?? "").toUpperCase()}`;
				cannot.push(`${left}${r.matchNote ? `    (${r.matchNote})` : ""}`);
			} else manual.push(`${line}    [manual]`);
		} else {
			// still unresolved in UI
			const left = `${padRight(r.std_id_raw ?? "", 10)}  ${(r.std_name_raw ?? "").toUpperCase()}`;
			unmatched.push(left);
		}
	}

	const sections = [];
	if (exact.length) {
		sections.push(
			`Names Having Exact Match`,
			"",
			...exact,
			"",
			`Number of Names With Perfect Match= ${exact.length}`,
			"",
		);
	}
	if (close.length) {
		sections.push(
			`Exact ID Match, Partial Name Match`,
			"",
			...close,
			"",
			`Number of Names With Exact ID Match, Partial Name Match=  ${close.length}`,
			"",
		);
	}
	if (manual.length) {
		sections.push(
			`Manually Matched`,
			"",
			...manual,
			"",
			`Number of Manual Matches=             ${manual.length}`,
			"",
		);
	}
	if (cannot.length) {
		sections.push(
			`Non-matchable Records`,
			"",
			...cannot,
			"",
			`Number of Non-matchable Records=      ${cannot.length}`,
			"",
		);
	}

	sections.push(
		`Number of remaining unmatched scanner records=   ${unmatched.length}`,
		...unmatched,
		"",
		"",
	);

	// Optional: students on classlist without scanner data (ID not in any std_id_raw)
	const rawIds = new Set(responses.map((r) => String(r.std_id_matched ?? "")).filter(Boolean));
	const missingFromScanner = classListFile
		.filter((r) => !rawIds.has(String(r[6])))
		.map((r) => `${r[4]},${r[3]},${r[6]}${r[2] ? `,${r[2]}` : ""}`)
		.sort();

	sections.push(`Class List identification of students without scanner data`);
	sections.push(...(missingFromScanner.length ? missingFromScanner : ["<none>"]));

	return headerLines.concat(sections).join("\n");
}
